import type { JSONSchema } from '@/api/types/schema'

/**
 * Analyzes a JSON schema to find sections with single score fields that can be used for filtering
 * @param jsonSchema - The JSON schema to analyze
 * @returns Array of valid filter sections with their available fields
 */
export function getValidFilterSections(jsonSchema?: unknown): Array<{
  id: string
  title: string
  fields: Array<{id: string, title: string}>
}> {
  if (!jsonSchema || typeof jsonSchema !== 'object' || jsonSchema === null) {
    return []
  }

  const schema = jsonSchema as JSONSchema
  const schemaProps = schema?.properties
  
  if (!schemaProps) {
    return []
  }

  const validSections: Array<{
    id: string
    title: string
    fields: Array<{id: string, title: string}>
  }> = []

  // Look through all top-level properties in the schema
  Object.keys(schemaProps).forEach(sectionKey => {
    // Skip non-object properties like "title"
    const sectionSchema = schemaProps[sectionKey]
    if (!sectionSchema || typeof sectionSchema !== 'object' || sectionSchema.type !== 'object') {
      return
    }

    const sectionProps = sectionSchema.properties
    if (!sectionProps) return

    const scoreFields: Array<{id: string, title: string}> = []

    // Look for subsections with score fields
    Object.keys(sectionProps).forEach(propKey => {
      const prop = sectionProps[propKey]
      if (!prop || typeof prop !== 'object' || prop.type !== 'object') return

      const propProperties = prop.properties
      if (!propProperties) return

      // Check if this property has a score field
      if (propProperties.score && propProperties.score.type === 'integer') {
        scoreFields.push({
          id: propKey,
          title: formatFieldName(propKey)
        })
      }
    })

    // Only include sections with exactly one score field
    if (scoreFields.length === 1) {
      validSections.push({
        id: sectionKey,
        title: formatFieldName(sectionKey),
        fields: scoreFields
      })
    }
  })

  return validSections
}

/**
 * Formats a string by replacing underscores with spaces and capitalizing each word
 * @param str - The string to format
 * @returns Formatted string with proper capitalization
 */
export function formatFieldName(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
}
